import React from 'react';

const EventStatus = ({ event, size = 'normal' }) => {
  const getStatusConfig = (status) => {
    const configs = {
      draft: {
        color: 'gray',
        icon: 'fas fa-edit',
        label: 'Draft'
      },
      published: {
        color: 'blue',
        icon: 'fas fa-eye',
        label: 'Published'
      },
      registration_open: {
        color: 'green',
        icon: 'fas fa-check-circle',
        label: 'Registration Open'
      },
      registration_closed: {
        color: 'orange',
        icon: 'fas fa-clock',
        label: 'Registration Closed'
      },
      ongoing: {
        color: 'blue',
        icon: 'fas fa-play-circle',
        label: 'Ongoing'
      },
      completed: {
        color: 'purple',
        icon: 'fas fa-flag-checkered',
        label: 'Completed'
      },
      cancelled: {
        color: 'red',
        icon: 'fas fa-times-circle',
        label: 'Cancelled'
      }
    };

    return configs[status] || configs.published;
  };

  // Determine the most relevant status to display
  const getDisplayStatus = () => {
    const now = new Date();
    const startDate = new Date(event.event_start_date);
    const endDate = new Date(event.event_end_date);
    
    // If event is cancelled, always show that
    if (event.status === 'cancelled') {
      return 'cancelled';
    }
    
    // If event is completed or past end date, show completed
    if (event.status === 'completed' || endDate < now) {
      return 'completed';
    }
    
    // If event is currently happening, show ongoing
    if (startDate <= now && endDate >= now) {
      return 'ongoing';
    }
    
    // If event is in the future, check registration status
    if (startDate > now) {
      if (event.registration_status === 'open') {
        return 'registration_open';
      } else if (event.registration_status === 'closed') {
        return 'registration_closed';
      }
    }
    
    // Default to the event's status
    return event.status;
  };

  const displayStatus = getDisplayStatus();
  const statusConfig = getStatusConfig(displayStatus);

  const sizeClass = size === 'small' ? 'status-badge-sm' : 
                   size === 'large' ? 'status-badge-lg' : '';

  return (
    <span 
      className={`status-badge status-${statusConfig.color} ${sizeClass}`}
      title={statusConfig.label}
    >
      <i className={statusConfig.icon}></i>
      <span className="status-text">{statusConfig.label}</span>
    </span>
  );
};

// Individual status components for specific use cases
export const RegistrationStatus = ({ event }) => {
  const { registration_status } = event;
  
  if (!registration_status || registration_status === 'not_available') {
    return null;
  }

  const configs = {
    not_started: {
      color: 'gray',
      icon: 'fas fa-clock',
      label: 'Registration Not Started',
      message: `Registration opens on ${new Date(event.registration_start_date).toLocaleDateString()}`
    },
    open: {
      color: 'green',
      icon: 'fas fa-check-circle',
      label: 'Registration Open',
      message: event.registration_end_date 
        ? `Registration closes on ${new Date(event.registration_end_date).toLocaleDateString()}`
        : 'Registration is currently open'
    },
    closed: {
      color: 'red',
      icon: 'fas fa-times-circle',
      label: 'Registration Closed',
      message: 'Registration period has ended'
    }
  };

  const config = configs[registration_status] || configs.closed;

  return (
    <div className={`registration-status status-${config.color}`}>
      <div className="status-indicator">
        <i className={config.icon}></i>
        <span className="status-label">{config.label}</span>
      </div>
      {config.message && (
        <div className="status-message">
          {config.message}
        </div>
      )}
    </div>
  );
};

export const EventCountdown = ({ event }) => {
  const now = new Date();
  const startDate = new Date(event.event_start_date);
  const endDate = new Date(event.event_end_date);
  
  if (endDate < now) {
    return (
      <div className="event-countdown completed">
        <i className="fas fa-flag-checkered"></i>
        <span>Event Completed</span>
      </div>
    );
  }
  
  if (startDate <= now && endDate >= now) {
    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    return (
      <div className="event-countdown ongoing">
        <i className="fas fa-play-circle"></i>
        <span>Event in Progress</span>
        {daysRemaining > 0 && (
          <small>({daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining)</small>
        )}
      </div>
    );
  }
  
  if (startDate > now) {
    const daysUntil = Math.ceil((startDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntil === 0) {
      return (
        <div className="event-countdown today">
          <i className="fas fa-calendar-day"></i>
          <span>Starts Today</span>
        </div>
      );
    }
    
    if (daysUntil === 1) {
      return (
        <div className="event-countdown tomorrow">
          <i className="fas fa-calendar-day"></i>
          <span>Starts Tomorrow</span>
        </div>
      );
    }
    
    return (
      <div className="event-countdown upcoming">
        <i className="fas fa-calendar-alt"></i>
        <span>Starts in {daysUntil} day{daysUntil !== 1 ? 's' : ''}</span>
      </div>
    );
  }
  
  return null;
};

export const TeamRegistrationProgress = ({ event }) => {
  if (!event.max_teams) {
    return null;
  }

  const progress = (event.registered_teams / event.max_teams) * 100;
  const spotsRemaining = event.max_teams - event.registered_teams;
  
  return (
    <div className="team-registration-progress">
      <div className="progress-header">
        <span className="progress-label">Team Registration</span>
        <span className="progress-count">
          {event.registered_teams}/{event.max_teams} teams
        </span>
      </div>
      
      <div className="progress-bar-container">
        <div className="progress-bar">
          <div 
            className={`progress-fill ${progress >= 100 ? 'full' : progress >= 80 ? 'high' : ''}`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          ></div>
        </div>
      </div>
      
      <div className="progress-footer">
        {spotsRemaining > 0 ? (
          <span className="spots-remaining">
            {spotsRemaining} spot{spotsRemaining !== 1 ? 's' : ''} remaining
          </span>
        ) : (
          <span className="spots-full">Event is full</span>
        )}
      </div>
    </div>
  );
};

export default EventStatus;